import { BackupOutlined, CloseOutlined } from "@material-ui/icons";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  AttachmentFilesCard,
  AutoCompleteWithSearch,
  CurrencyNumberInput,
  FormLabel,
  ModalError,
  PrimaryButton,
  SecondaryButton,
  SelectInput,
} from "components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatFileSize, getErrors, stringTruncate } from "utils";

const ImportDocumentModal = ({
  modalTitle,
  open,
  onClose,
  onSubmit,
  exchangeRate,
  loadingButton,
  fileType,
  multiple,
  downloadFile,
  onChangePublisher,
  onChangeFormat,
  roleType,
  preload,
  maxWidth,
  maxTruncate,
  downloadComposerTemplate,
  handleDownloadButton,
  downloadButtonLabel,
  loadingDownloadButton,
  disabledDownloadButton,
  handleDragFiles,
  onChangeSalesInvoice,
  selectedInvoices,
  setSelectedInvoices,
}) => {
  const classes = globalStyles();
  const dialogRef = useRef();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [societyFormat, setSocietyFormat] = useState("asaba");
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [payload, setPayload] = useState(0);
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [documentFiles, setDocumentFiles] = useState([]);
  const [downloadedFile, setDownloadedFile] = useState("");
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [optionSalesInvoice, setOptionSalesInvoice] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const onDrop = useCallback(
    acceptedFiles => {
      if (handleDragFiles) {
        handleDragFiles(acceptedFiles);
      }

      if (multiple) {
        setDocumentFiles(prev => [...prev, ...acceptedFiles]);
      } else {
        setDocumentFiles(acceptedFiles);
      }
    },
    [handleDragFiles, multiple]
  );
  const handleRemoveFile = idx => {
    setDocumentFiles(prevFiles => prevFiles.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    onSubmit(documentFiles, payload);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: fileType,
    multiple: multiple,
  });
  const downloadTemplate = async () => {
    try {
      setLoadingDownload(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/${
          downloadComposerTemplate
            ? "composer/export"
            : "song/download-template-catalogue"
        }`,
        { responseType: downloadComposerTemplate ? "blob" : "json", headers }
      );
      if (downloadComposerTemplate) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "template.xlsx");
        document.body.appendChild(link);
        link.click();
      } else {
        setDownloadedFile(res?.data?.data);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingDownload(false);
    }
  };
  const getOptionPublisher = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(`${hardBaseUrl}/publisher`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.publisher_id,
      }));
      setOptionPublisher(prev =>
        queryParams.page === 1 ? newOptions : [...prev, ...newOptions]
      );
      const totalPages = Math.ceil(meta?.total / meta?.limit);
      setHasMore(queryParams?.page < totalPages);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const getOptionSalesInvoice = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/upload/list-sales-invoice`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.sales_invoice_id,
      }));
      setOptionSalesInvoice(prev => {
        const combined =
          queryParams.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined?.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };

  const dropzoneStyle = useMemo(() => {
    return isDragActive ? classes?.dragActive : "";
  }, [isDragActive, classes.activeStyle]);

  useEffect(() => {
    downloadFile && !downloadComposerTemplate && downloadTemplate();
  }, []);
  useEffect(() => {
    (onChangePublisher && getOptionPublisher()) ||
      (onChangeSalesInvoice && getOptionSalesInvoice());
  }, [queryParams]);
  useEffect(() => {
    if (preload) {
      setDocumentFiles(preload);
    }
  }, [preload]);

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={!loadingButton && onClose}
      fullWidth
      maxWidth={maxWidth || "xs"}
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography fontWeight={700}>
              {modalTitle || "Import Document"}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={!loadingButton && onClose}>
              <CloseOutlined />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <FormLabel label="Upload Document" />
        <div
          {...getRootProps({
            className: `${classes.dragFilesContainer} ${dropzoneStyle}`,
          })}
        >
          <input {...getInputProps()} />
          <Box color="grey">
            <BackupOutlined className={classes?.uploadIcon} />
          </Box>
          <Typography>
            {isDragActive
              ? "Drop the files here..."
              : "Drag files or click to upload"}
          </Typography>
        </div>
        <Grid container direction="column" my="16px" rowSpacing={2}>
          {onChangeSalesInvoice && (
            <Grid item>
              <FormLabel label="Sales Invoice No" />
              <AutoCompleteWithSearch
                placeholder="Sales Invoice No"
                options={optionSalesInvoice}
                value={selectedInvoices || null}
                onChange={value => setSelectedInvoices(value)}
                multiple
                uniqueKey="id"
                isOptionEqualToValue={(option, value) =>
                  option.sales_invoice_id === value.sales_invoice_id
                }
                inputValue={queryParams?.search}
                onInputChange={value =>
                  handleChangeQueryParams(value, "search")
                }
                optionLabel="sales_invoice_number"
                setQueryParams={setQueryParams}
                hasMore={hasMore}
                loading={loadingFilter}
                width="100%"
              />
            </Grid>
          )}
          {(downloadFile || handleDownloadButton) && (
            <Grid item>
              {handleDownloadButton ? (
                <PrimaryButton
                  label={
                    downloadButtonLabel ||
                    `${
                      loadingDownloadButton ? "Downloading" : "Download"
                    } Document`
                  }
                  onClick={handleDownloadButton}
                  disabled={disabledDownloadButton || loadingDownloadButton}
                  loading={loadingDownloadButton}
                />
              ) : (
                <a href={!downloadComposerTemplate && downloadedFile} download>
                  <PrimaryButton
                    label={
                      downloadComposerTemplate && loadingDownload
                        ? "Downloading Template"
                        : "Download Template"
                    }
                    onClick={() =>
                      downloadComposerTemplate && downloadTemplate()
                    }
                    disabled={downloadComposerTemplate && loadingDownload}
                    loading={downloadComposerTemplate && loadingDownload}
                  />
                </a>
              )}
            </Grid>
          )}
          {roleType === "society" && (
            <Grid item>
              <SelectInput
                value={societyFormat}
                onChange={e => {
                  setSocietyFormat(e?.target?.value);
                  onChangeFormat(e?.target?.value);
                }}
                options={optionSociety}
                optionKey="id"
                optionLabel="name"
                width="100%"
                label="Society Format"
              />
            </Grid>
          )}
          {onChangePublisher && societyFormat === "asaba" && (
            <Grid item>
              <AutoCompleteWithSearch
                label="Publisher"
                options={optionPublisher}
                optionLabel="name"
                loading={loadingFilter}
                value={
                  optionPublisher?.find(
                    option => option.publisher_id === selectedPublisher
                  ) || null
                }
                onChange={item => {
                  const value = item?.publisher_id;
                  setSelectedPublisher(value);
                  onChangePublisher(value);
                }}
                inputValue={queryParams?.search}
                onInputChange={value =>
                  handleChangeQueryParams(value, "search")
                }
                setQueryParams={setQueryParams}
                hasMore={hasMore}
                width="100%"
              />
            </Grid>
          )}
        </Grid>
        <Box mt="16px">
          <FormLabel label="Uploaded File" />
          <Grid container direction="column" rowSpacing={2} mb="16px">
            {documentFiles.length > 0 &&
              documentFiles?.map(({ name, size, url }, index) => (
                <Grid item key={index}>
                  <AttachmentFilesCard
                    fileName={name && stringTruncate(name, maxTruncate || 20)}
                    fileSize={size && formatFileSize(size)}
                    filePath={url}
                    handleRemove={() => handleRemoveFile(index)}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
        {exchangeRate && (
          <Box my="16px">
            <FormLabel label="Exchange Rate" />
            <CurrencyNumberInput
              startAdornment="Rp"
              value={payload}
              onChange={value => setPayload(value)}
              decimalScale={2}
            />
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          onClick={onClose}
          disabled={loadingButton}
        />
        <PrimaryButton
          label={loadingButton ? "Uploading" : "Upload"}
          disabled={loadingButton}
          loading={loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};
const optionSociety = [
  {
    id: "asaba",
    name: "ASABA",
  },
  {
    id: "wami",
    name: "WAMI",
  },
];

export default ImportDocumentModal;
